import { ICellRendererParams } from "ag-grid-community";
import React, { MouseEvent, FC, useRef, useContext } from "react";
import styles from "components/portfolio/cellRenderer/ExpandPlanRenderer.module.scss";
import ArrowPopper from "components/app/ArrowPopper";
import { Skeleton } from "@mui/material";
import moment from "moment";
import dynamic from "next/dynamic";
import "moment-timezone";
import { KeyboardArrowDown, OpenInNew } from "@mui/icons-material";
import Link from "next/link";
import { useState } from "react";
import { useEffect } from "react";
import QVRDialog from "components/summary/index/QVR/QVRDialog";
import { AuthProvider } from "pages";
import { FEATURE_KEY } from "constant";
import { useQVRByAccountKey } from "components/summary/hooks";
import { mutate } from "swr";
import { classHOC } from "components/app/ClassWrapper";

const ExpandPlanRenderer: FC<ICellRendererParams> = ({ api, data, node }) => {
  const epCellRef = useRef<HTMLDivElement>(null);
  const epPopRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);
  const { qvrFiscalQuarter } = data;
  const { data: qvrList, error } = useQVRByAccountKey(
    open || anchorEl ? data.accountKey : undefined
  );

  const onClose = () => {
    setOpen(false);
  };
  const onOpen = () => {
    setOpen(true);
  };

  if (node.rowPinned) return <></>;

  useEffect(() => {
    const closePop = (e: Event) => {
      if (
        !epPopRef.current?.contains(e.target as Node) &&
        epPopRef.current !== e.target &&
        !epCellRef.current?.contains(e.target as Node)
      ) {
        setAnchorEl(null);
      }
    };
    document.addEventListener("click", closePop);
    return () => {
      document.removeEventListener("click", closePop);
    };
  }, []);

  const handlePopoverOpen = (event: MouseEvent<HTMLElement>) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const authDetail = useContext(AuthProvider);
  const canEditQVR = authDetail.features.includes(FEATURE_KEY.EDIT_QVR) && !qvrFiscalQuarter;

  const canViewQVR = authDetail.features.includes(FEATURE_KEY.VIEW_QVR) && qvrFiscalQuarter;

  const canPopup =
    (qvrFiscalQuarter || data.accountStatus === "Active") && (canEditQVR || canViewQVR);

  return (
    <div
      className="wrapper"
      style={canPopup ? { cursor: "pointer" } : undefined}
      onClick={canPopup ? handlePopoverOpen : undefined}
      ref={epCellRef}
    >
      <div style={{ display: "flex", alignItems: "center" }}>
        {qvrFiscalQuarter ? (
          <>
            <span style={{ color: "green" }}>Yes &nbsp;</span>
            <span>({qvrFiscalQuarter})</span>
          </>
        ) : (
          <span style={{ color: "red" }}>No</span>
        )}
        {canPopup ? <KeyboardArrowDown style={{ fontSize: "1rem" }} /> : null}
      </div>
      {anchorEl && !open ? (
        <ArrowPopper
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          placement="left"
          disablePortal={false}
        >
          <div ref={epPopRef}>
            {data.accountStatus === "Active" && !qvrFiscalQuarter ? (
              <div onClick={() => onOpen()}>
                <a
                  className={styles.pointWrapper}
                  style={{ display: "flex", alignItems: "center" }}
                  target="_blank"
                  rel="noreferrer"
                >
                  <OpenInNew style={{ marginRight: 4, fontSize: "1rem" }} />
                  Add
                </a>
              </div>
            ) : error ? (
              <div
                className={styles.loadingWrapper}
                style={{ display: "flex", flexDirection: "column" }}
              >
                {Array.from({ length: 4 }).map((x, i) => (
                  <Skeleton
                    key={`${x}${i}`}
                    width={300}
                    height={32}
                    variant="rectangular"
                    style={{ margin: "6px 12px" }}
                  />
                ))}
              </div>
            ) : qvrList === undefined ? (
              <div>loading...</div>
            ) : qvrList?.length ? (
              <div
                style={{ overflow: "auto" }}
                className={
                  qvrList.length > 0
                    ? styles.discoveryRecordWrapper
                    : `${styles.discoveryRecordWrapper} ${styles.loadingWrapper}`
                }
              >
                {qvrList.length > 0 ? (
                  <>
                    {qvrList.map((item) => (
                      <div
                        key={item.id}
                        className={styles.discoveryContainer}
                      >
                        <span className={styles.singleDiscoveryRecord}>{item.fiscalQuarter}</span>
                        <span className={styles.discoveryRecordDate}>
                          {moment(item.createdDate).tz("America/Los_Angeles").format("MM/DD/YYYY")}
                        </span>
                      </div>
                    ))}
                    <Link
                      href={`/spng/summary?accountId=${data?.accountId}#quarterViewReview`}
                      passHref={true}
                      legacyBehavior
                    >
                      <a
                        className={styles.pointWrapper}
                        style={{ display: "flex", alignItems: "center" }}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <OpenInNew style={{ marginRight: 4, fontSize: "1rem" }} /> Show Details
                      </a>
                    </Link>
                  </>
                ) : (
                  <div>Sorry, fail to load data</div>
                )}
              </div>
            ) : null}
          </div>
        </ArrowPopper>
      ) : null}
      <QVRDialog
        open={open}
        onClose={onClose}
        type="Add New"
        accountId={data?.accountId}
        accountKey={data?.accountKey}
        mutate={() =>
          mutate(
            `/api/dataservice/v1/accounts/${data?.accountKey}/qvrs?sort=createdDate:desc`
          ).then((x) => {
            node.updateData({
              ...data,
              qvrFiscalQuarter: x?.[0]?.fiscalQuarter,
            });
            api.redrawRows({ rowNodes: [node] });
          })
        }
      />
    </div>
  );
};
export default dynamic(() => Promise.resolve(classHOC(ExpandPlanRenderer)), {
  ssr: false,
});
